import { useAuth } from "@clerk/react";
import { Link } from "@tanstack/react-router";

import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { Button, type ButtonProps, buttonVariants } from "@/web/components/primitives/button";
import { cn } from "@/web/lib/styling";

type SignInButtonProps = ButtonProps & {
  label?: string;
};

/**
 * Renders the sign-in CTA, or — once the user is authenticated — a "Dashboard"
 * button (same styling) that links to their profile. Reused styling props apply
 * to both states so callers get a consistent button in the navbar and CTA.
 */
export function SignInButton({
  label = "Continue",
  variant,
  size,
  color,
  className,
  ...buttonProps
}: SignInButtonProps) {
  const { isLoaded, isSignedIn } = useAuth();

  if (isLoaded && isSignedIn) {
    // The Button component renders multiple children internally, so it can't be
    // used as a Slot (`asChild`). Style the Link with the variants directly.
    return (
      <Link
        to="/profile/smb"
        className={cn(
          buttonVariants({ variant, size, color, className }),
          "focus-visible:ring-2 focus-visible:ring-black/20",
        )}
      >
        {label}
      </Link>
    );
  }

  return (
    <AuthnFormDialog
      trigger={
        <Button variant={variant} size={size} color={color} className={className} {...buttonProps}>
          {label}
        </Button>
      }
      title="Welcome"
      description="Continue with Google or email"
    />
  );
}
